/**
 * @file Progressive image component (pairs with ProgressivePhotoGrid).
 */

import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { IMAGE_SIZE_CODE } from '../../constants/image-size-codes';
import { getThumbBySize } from '../../util/photo';
import Spinner from '../global/Spinner';
import { getAsset } from '../../util/assets';
import withPhotoUploadCache from '../../apps/pro/components/hoc/withPhotoUploadCache';

class ProgressivePhoto extends PureComponent {
  static propTypes = {
    thumbnails: PropTypes.arrayOf(
      PropTypes.shape({
        width: PropTypes.number,
        height: PropTypes.number,
        url: PropTypes.string,
        size_code: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)),
      }),
    ),
    size: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)),
    className: PropTypes.string,
    children: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.arrayOf(PropTypes.node),
      PropTypes.string,
    ]),
    onImageLoad: PropTypes.func,
    progressive: PropTypes.bool,
    allowFallbackThumbnail: PropTypes.bool,
    style: PropTypes.object,
    imgProps: PropTypes.object,
  };

  static defaultProps = {
    size: IMAGE_SIZE_CODE.MEDIUM2X,
    progressive: false,
    onImageLoad: () => {},
  };

  state = {
    visible: false,
  };

  onImageLoad = () => {
    const { onImageLoad } = this.props;
    this.setState(
      () => ({ visible: true }),
      () => {
        onImageLoad();
      },
    );
  };

  getImageThumbnails = () => {
    const { thumbnails, size, allowFallbackThumbnail } = this.props;

    return {
      thumb: getThumbBySize(thumbnails, size, allowFallbackThumbnail),
      minThumb: getThumbBySize(thumbnails, IMAGE_SIZE_CODE.SMALL),
    };
  };

  render() {
    const { visible } = this.state;
    const {
      className,
      style = {},
      children,
      progressive,
      imgProps,
    } = this.props;
    const { thumb, minThumb } = this.getImageThumbnails();

    const classes = classNames({
      'photo-progressive': true,
      [className]: !!className,
    });

    return (
      <div className={classes} style={{ ...style }}>
        {thumb ? (
          <Fragment>
            {minThumb && progressive && (
              <div
                className="photo-progressive-placeholder"
                style={{
                  backgroundImage: `url(${minThumb.url})`,
                }}
              />
            )}

            <img
              className="photo-progressive-img"
              src={thumb.url}
              alt=""
              style={{
                opacity: visible ? 1 : 0,
              }}
              onLoad={this.onImageLoad}
              {...imgProps}
            />

            {!progressive && !visible && (
              <Spinner size="xs" className="photo-progressive-spinner" />
            )}

            {children}
          </Fragment>
        ) : (
          <div className="image-cover-nothumb">
            <img src={getAsset('/waldo-icon.svg')} alt="" />
            <span>
              This photo is currently processing, please check back a bit later!
            </span>
          </div>
        )}
      </div>
    );
  }
}

export { ProgressivePhoto as ProgressivePhotoComponent };

export default withPhotoUploadCache(ProgressivePhoto);
